import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, Subject, BehaviorSubject, forkJoin } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { TaskUnit } from './models/task-unit';
import { TaskUnitList, TaskComponentList } from './models/task-unitlist';
import { AppConfig } from '../../../../app/app.config';
import { PriceList } from './models/pricelist';
import { InvoicingProposal } from './models/invoicing-proposal';
import { PriceComparison } from './models/price-comparison';
import { TaskComponent, TaskOpenComponent } from './models/task-component';
import { Inventory } from '../../../shared/models/inventory';
import { ComponentType } from 'src/app/shared/models/component-type';
import { UsableInventoryItem } from 'src/app/shared/models/usable-inventory-item';
import { EndCustomerInvoice } from './models/end-customer-invoice';
export class TaskUnitODataResult {
    public value: TaskUnit[];
}

export class PriceListODataResult {
    public value: PriceList[];
}

export class InvoiceProposalODataResult {
    public value: InvoicingProposal[];
}

export class EndCustomerInvoiceODataResult {
    public value: EndCustomerInvoice[];
}

export interface endCustomerLicense {
    value: boolean;
}

@Injectable({
    providedIn: 'root',
})

export class TaskUnitsService {
    private urlIwmBackend: string = this.config.backendIwmUri + 'Api/';
    private urlEndCustomerInvoice: string = 'EndCustomerInvoiceApi/EndCustomerInvoices?$filter=TaskId eq {0}';
    private endCustomerInvoiceLicenseUrl = 'EndCustomerInvoiceApi/EndCustomerInvoices/CheckLicenseForEndCustomerInvoice(taskId={0})';

    private urlInvoicingProposal: string = 'InvoicingProposalApi/ListInvoicingProposals?$filter=TaskId eq {0}';

    private urlTaskListUnits: string = 'Api/ListUnits?$filter=TaskId eq {0}';
    private urlTaskOpenUnits: string = 'Api/OpenUnits?$filter=TaskId eq {0}';

    private urlPriceComparison: string = 'Tasks({0})/Default.PriceComparison()';

    private urlUnitLists: string = 'UnitLists/Default.GetUnitListsForTask(taskId={0})';
    private urlUnitListSingle: string = 'UnitLists({0})';
    private urlComponentLists: string = 'TaskComponentLists/Default.GetComponentListsForTask(taskId={0})';

    private urlComponentHandlingLicense: string = 'TaskComponentLists/Default.CheckComponentHandlingLicenseForTask(taskId={0})';
    private urlInventoryProductLicense: string = 'TaskComponentLists/Default.CheckInventoryProductLicenseForTask(taskId={0})';

    private urlOpenUnitListUnits: string = 'OpenUnits?$filter=UnitListId eq {0}&$expand=CostAccountings';

    private urlDeleteUnit: string = 'OpenUnits({0})';
    private urlSaveUnit: string = 'OpenUnits({0})';

    private urlCreateUnit: string = 'OpenUnits?$expand=CostAccountings';

    private urlUnitListUnits: string = 'ListUnits?$filter=UnitListId eq {0}&$expand=CostAccountings';

    private urlDeleteListUnit: string = 'ListUnits({0})';
    private urlCreateListUnit: string = 'ListUnits?$expand=CostAccountings';

    private urlSaveListUnit: string = 'ListUnits({0})';
    private urlListUnit: string = 'ListUnits({0})';
    private urlOpenUnit: string = 'OpenUnits({0})';

    private urlResetUnitPrice: string = 'ListUnits({0})/Default.RestoreUnitPrice()';
    private urlResetComponentPrice: string = 'ListComponents()/Default.RestoreComponentPrices()';
    private urlResetInventoryComponentPrice: string = 'InventoryComponents()/Default.RestoreComponentPrices()';


    private urlPriceListUnits: string =
        this.config.backendAmgUri + 'Api/PriceLists({0})/Default.GetPriceListUnits(companyId={1})';
    private urlPriceListBudgetingUnits: string =
        this.config.backendAmgUri + 'Api/PriceLists({0})/Default.GetBudgetedPriceListUnits()';

    private urlDefaultPricelist: string =
        this.config.backendAmgUri + 'Api/PriceLists/GetDefaultPriceList';


    private urlDefaultBudgetPricelist: string =
        this.config.backendAmgUri + 'Api/PriceLists/GetDefaultBudgetPriceList';

    private urlDefaultComponentPricelist: string =
        this.config.backendAmgUri + 'Api/PriceLists/GetDefaultComponentPriceList';

    private urlDefaultInventory: string =
        this.config.backendAmgUri + 'Api/PriceLists/GetDefaultInventory';

    private amgUnitUrl =
        this.config.backendAmgUri + 'Api/Units({0})';

    private amgUnitExpandUrl =
        this.config.backendAmgUri + 'Api/Units({0})?%24expand=Group%2CVersion';



    private urlUnitList: string = 'UnitLists';

    private urlComponentListSingle: string = 'TaskComponentLists({0})';
    private urlComponentList: string = 'TaskComponentLists';

    private urlOpenComponents: string = 'OpenComponents';
    private urlListComponents: string = 'ListComponents';
    private urlInventoryComponents: string = 'InventoryComponents';

    private urlGetPriceLists: string = '';

    private urlInventoryApi: string = 'InventoryApi/';
    private urlInventories: string = 'Inventories/';
    private urlUsableInventoryItems: string = 'UsableInventoryItems/';

    public reloadUnitList: Subject<any>;

    public unitListsChanged: Subject<any> = new Subject();

    // observable for passing up-to-date rowSumType to components that need it
    public readonly rowSumType$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    constructor(private http: HttpClient,
        private config: AppConfig) {
        this.urlGetPriceLists = config.backendAmgUri + 'Api/PriceLists';
    }

    public setRowSumType(value: string) {
        this.rowSumType$.next(value);
    }

    public addSuppliedAmount(targetUnitId: string, amount: number, modified: Date, newDescription: string,
        isListUnit: boolean): Observable<any> {
        const controllerUrlPart = isListUnit ? this.urlListUnit : this.urlOpenUnit;
        const url = `${this.urlIwmBackend}${controllerUrlPart.replace('{0}', targetUnitId)}/Default.AddSuppliedAmountEntry`;

        const formattedAmount = parseFloat(amount.toString().replace(',', '.'));
        const payload = { suppliedAmount: formattedAmount, modified: modified, description: newDescription };

        return this.http.post(url, payload);
    }

    public addSuppliedAmountComponents(targetUnitId: string, amount: number, modified: Date, newDescription: string,
        componentType: ComponentType): Observable<any> {
        let controllerUrlPart: string;
        if (componentType === ComponentType.OpenComponent) {
            controllerUrlPart = this.urlOpenComponents;
        }
        else if (componentType === ComponentType.ListComponent) {
            controllerUrlPart = this.urlListComponents;
        }
        else {
            controllerUrlPart = this.urlInventoryComponents;
        }

        const url = `${this.urlIwmBackend}${controllerUrlPart}/${targetUnitId}/Default.AddSuppliedAmountEntry`;

        const formattedAmount = parseFloat(amount.toString().replace(',', '.'));
        const payload = { suppliedAmount: formattedAmount, modified: modified, description: newDescription };

        return this.http.post(url, payload);
    }

    public addToPaymentPlan(unitList: TaskUnitList): Observable<any> {
        const url = `${this.urlIwmBackend + this.urlUnitList}(${unitList.Id})/Default.AddToPaymentPlan`;

        return this.http.post(url, {}, {
            observe: 'response'
        }).pipe(
            map((result) => {
                return result.status === 204; // No Content
            })
        );
    }

    public setSuppliedAmount(targetSuppliedId: string, newAmount: number, newDate: Date, newDescription: string,
        isListUnit: boolean) {
        const controllerUrlPart = isListUnit ? 'ListUnits' : 'OpenUnits';
        const url = `${this.urlIwmBackend}${controllerUrlPart}/Default.EditSuppliedAmountEntry`

        const formattedAmount = parseFloat(newAmount.toString().replace(',', '.'));
        const payload = { id: targetSuppliedId, suppliedAmount: formattedAmount, modified: newDate, description: newDescription };

        return this.http.post(url, payload);
    }

    public setSuppliedAmountComponents(targetSuppliedId: string, newAmount: number, newDate: Date, newDescription: string,
        componentType: ComponentType) {
        let controllerUrlPart: string;
        if (componentType === ComponentType.OpenComponent) {
            controllerUrlPart = this.urlOpenComponents;
        }
        else if (componentType === ComponentType.ListComponent) {
            controllerUrlPart = this.urlListComponents;
        }
        else {
            controllerUrlPart = this.urlInventoryComponents;
        }

        const url = `${this.urlIwmBackend}${controllerUrlPart}/Default.EditSuppliedAmountEntry`

        const formattedAmount = parseFloat(newAmount.toString().replace(',', '.'));
        const payload = { id: targetSuppliedId, suppliedAmount: formattedAmount, modified: newDate, description: newDescription };

        return this.http.post(url, payload);
    }

    public getDefaultPriceList(): Observable<any> {
        return this.http.get(this.urlDefaultPricelist);
    }


    public getDefaultBudgetPriceList(): Observable<any> {
        return this.http.get(this.urlDefaultBudgetPricelist);
    }

    public getDefaultComponentPriceList(): Observable<any> {
        return this.http.get(this.urlDefaultComponentPricelist);
    }

    public getDefaultInventory(): Observable<any> {
        return this.http.get(this.urlDefaultInventory);
    }

    public patchUnitListNameOrPriceList(unitList: TaskUnitList): Observable<any> {
        if (!unitList) {
            throw Error('The given unit list was undefined or null and cannot be patched');
        }
        const url = this.urlIwmBackend + this.urlUnitList + '(' + unitList.Id + ')/Default.ChangeUnitListProperties'
        const body = {
            Name: unitList.Name,
            PriceListId: unitList.PriceListId,
            PriceListName: unitList.PriceListName
        }
        return this.http.post(url, body).pipe(
            tap(() => {
                if (this.unitListsChanged) {
                    this.unitListsChanged.next(void 0);
                }
            })
        );
    }

    public changeComponentListProperties(unitList: TaskComponentList): Observable<any> {
        if (!unitList) {
            throw Error('The given unit list was undefined or null and cannot be patched');
        }
        const url = this.urlIwmBackend + this.urlComponentList + '(' + unitList.Id + ')/Default.ChangeComponentListProperties'
        const body = {
            Name: unitList.Name,
            PriceListId: unitList.PriceListId,
            InventoryId: unitList.InventoryId
        }
        return this.http.post(url, body, {
            observe: 'response'
        }).pipe(
            tap(() => {
                if (this.unitListsChanged) {
                    this.unitListsChanged.next(void 0);
                }
            }), map((result) => {
                return result.status === 204; // No Content
            })
        );
    }

    public getListUnit(unitId: string): Observable<TaskUnit> {
        const url = this.urlIwmBackend + this.urlListUnit.replace('{0}', unitId);
        return this.http.get<TaskUnit>(url).pipe(
            map(res => {
                const unit = new TaskUnit().fillFromJson(res);
                return unit;
            })
        );
    }

    public getOpenUnit(unitId: string): Observable<TaskUnit> {
        const url = this.urlIwmBackend + this.urlOpenUnit.replace('{0}', unitId);
        return this.http.get<TaskUnit>(url).pipe(
            map(res => {
                const unit = new TaskUnit().fillFromJson(res);
                return unit;
            })
        );
    }

    public getPriceComparisonData(taskId: string): Observable<PriceComparison[]> {
        const url = this.urlIwmBackend + this.urlPriceComparison.replace('{0}', taskId);
        return this.http.get<PriceComparison[]>(url)
            .pipe(map((x: any) => x?.value));
    }

    public getUnitLists(taskId: string, useSort: boolean = false, filterString: string = null): Observable<TaskUnitList[]> {
        let url = this.urlIwmBackend + this.urlUnitLists.replace('{0}', taskId);
        let options = [];

        if (useSort) {
            options.push('$orderby=Created desc');
        }
        else {
            options.push('$orderby=Name asc, Created asc');
        }
        if (filterString) {
            options.push(filterString);
        }

        if (options.length > 0) {
            url += `?${options.join('&')}`
        }
        return this.http.get(url)
            .pipe(map((x: any) => x?.value));
    }

    public getUnitList(id: string): Observable<TaskUnitList> {
        const url = this.urlIwmBackend + this.urlUnitListSingle.replace('{0}', id);

        return this.http.get<TaskUnitList>(url);
    }

    public getComponentList(id: string): Observable<TaskComponentList> {
        const url = this.urlIwmBackend + this.urlComponentListSingle.replace('{0}', id);
        return this.http.get<TaskComponentList>(url);
    }

    public getComponentLists(taskId: string, useSort: boolean = false): Observable<any> {
        var url = this.urlIwmBackend + this.urlComponentLists.replace('{0}', taskId);
        if (useSort) {
            url = url + '?$orderby=Created desc';
        }
        else {
            url = url + '?$orderby=Name asc, Created asc';
        }
        return this.http.get(url)
            .pipe(map((x: any) => x?.value));
    }

    public checkComponentHandlingLicense(taskId: string): Observable<boolean> {
        var url = this.urlIwmBackend + this.urlComponentHandlingLicense.replace('{0}', taskId);
        return this.http.get(url)
            .pipe(map((x: any) => x?.value));
    }

    public getOpenComponent(componentId: string): Observable<TaskComponent> {
        const url = `${this.urlIwmBackend}${this.urlOpenComponents}/${componentId}`;

        return this.http.get<any>(url)
            .pipe(
                map((component: TaskComponent) => {
                    component.NewSuppliedEntryDescription = '';
                    return component;
                }));
    }

    public getListComponent(componentId: string): Observable<TaskComponent> {
        const url = `${this.urlIwmBackend}${this.urlListComponents}/${componentId}`;

        return this.http.get<any>(url)
            .pipe(
                map((component: TaskComponent) => {
                    component.NewSuppliedEntryDescription = '';
                    return component;
                }));
    }

    public getInventoryComponent(componentId: string): Observable<TaskComponent> {
        const url = `${this.urlIwmBackend}${this.urlInventoryComponents}/${componentId}`;

        return this.mapICToTaskComponent(this.http.get<any>(url));
    }

    public getComponentListOpenComponents(componentListId: string): Observable<Array<TaskComponent>> {
        const url = `${this.urlIwmBackend}${this.urlOpenComponents}?$filter=ComponentListId eq ${componentListId}&$expand=CostAccountings`;
        return this.http.get<any>(url)
            .pipe(map((x: any) => x?.value),
                map((components: Array<TaskComponent>) => {
                    return components.map((component: TaskComponent) => {
                        component.NewSuppliedEntryDescription = ''
                        return component;
                    });
                }));
    }

    public getComponentListListComponents(componentListId: string): Observable<Array<TaskComponent>> {
        const url = `${this.urlIwmBackend}${this.urlListComponents}?$filter=ComponentListId eq ${componentListId}&$expand=CostAccountings`;
        return this.http.get<any>(url)
            .pipe(map((x: any) => x?.value),
                map((components: Array<TaskComponent>) => {
                    return components.map((component: TaskComponent) => {
                        component.NewSuppliedEntryDescription = ''
                        return component;
                    });
                })
            );
    }

    public getComponentListInventoryComponents(componentListId: string): Observable<Array<TaskComponent>> {
        const url = `${this.urlIwmBackend}${this.urlInventoryComponents}?$filter=ComponentListId eq ${componentListId}&$expand=CostAccountings`;
        return this.mapICArrayToTaskComponentArray(this.http.get<any>(url));
    }

    public getOpenUnitListUnits(unitListId: string): Observable<Array<TaskUnit>> {

        const url = this.urlIwmBackend + this.urlOpenUnitListUnits.replace('{0}', unitListId);

        return this.http.get<TaskUnitODataResult>(url)
            .pipe(
                map((x: any) => x?.value),
                map((units: Array<TaskUnit>) => {
                    return units.map((unit: TaskUnit) => new TaskUnit().fillFromJson(unit));
                })
            );
    }

    public getUnitListUnits(unitListId: string): Observable<Array<TaskUnit>> {

        const url = this.urlIwmBackend + this.urlUnitListUnits.replace('{0}', unitListId);

        return this.http.get<TaskUnitODataResult>(url)
            .pipe(
                map((x: any) => x?.value),
                map((units: Array<TaskUnit>) => {
                    return units.map((unit: TaskUnit) => {
                        unit.IsListUnit = true;
                        return new TaskUnit().fillFromJson(unit);
                    });
                })
            );
    }

    public getPriceListUnits(priceListId: string, companyId: string = null): Observable<any> {
        const url = this.urlPriceListUnits.replace('{0}', priceListId).replace('{1}', companyId);

        return this.http.get(url)
            .pipe(
                map((x: any) => x?.value)
            );
    }

    public getPriceListComponents(priceListId: string): Observable<any> {
        const url = this.config.backendAmgUri + `Api/PriceLists(${priceListId})/Default.GetPriceListComponents`
        return this.http.get(url)
            .pipe(
                map((x: any) => x?.value)
            );
    }

    public getPriceListBudgetingUnits(priceListId: string): Observable<any> {
        const url = this.urlPriceListBudgetingUnits.replace('{0}', priceListId);

        return this.http.get(url)
            .pipe(
                map((x: any) => x?.value)
            );
    }

    public createUnitList(unitList: TaskUnitList): Observable<any> {
        const url = this.urlIwmBackend + this.urlUnitList;
        return this.http.post(url, unitList).pipe(tap(result => {
            if (result && this.unitListsChanged) {
                this.unitListsChanged.next(void 0);
            }
        }));
    }

    public createComponentList(unitList: TaskComponentList): Observable<any> {
        const url = this.urlIwmBackend + this.urlComponentList;
        return this.http.post(url, unitList).pipe(tap(result => {
            if (result && this.unitListsChanged) {
                this.unitListsChanged.next(void 0);
            }
        }));
    }

    public deleteTaskUnit(unitId: string, openUnit: boolean): Observable<any> {
        if (openUnit) { return this.deleteUnit(unitId); } else { return this.deleteListUnit(unitId); }
    }

    public deleteUnit(unitId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlDeleteUnit.replace('{0}', unitId);
        return this.http.delete(url);
    }

    public deleteListUnit(unitId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlDeleteListUnit.replace('{0}', unitId);
        return this.http.delete(url);
    }

    public deleteTaskComponent(componentId: string, componentType: ComponentType): Observable<any> {
        if (componentType === ComponentType.OpenComponent) {
            return this.deleteComponent(componentId);
        }
        else if (componentType === ComponentType.ListComponent) {
            return this.deleteListComponent(componentId);
        }
        else {
            return this.deleteInventoryComponent(componentId);
        }
    }

    public deleteComponent(componentId: string): Observable<any> {
        const url = `${this.urlIwmBackend}/OpenComponents(${componentId})`;
        return this.http.delete(url);
    }

    public deleteListComponent(componentId: string): Observable<any> {
        const url = `${this.urlIwmBackend}/ListComponents(${componentId})`;
        return this.http.delete(url);
    }

    public deleteInventoryComponent(componentId: string): Observable<any> {
        const url = `${this.urlIwmBackend}${this.urlInventoryComponents}(${componentId})`;
        return this.http.delete(url);
    }

    public deleteUnitList(unitListId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlUnitList + '(' + unitListId + ')';
        return this.http.delete(url, {
            observe: 'response'
        }).pipe(
            tap(() => {
                if (this.unitListsChanged) {
                    this.unitListsChanged.next(void 0);
                }
            }), map((result) => {
                return result.status === 204; // No Content
            })
        );
    }

    public deleteComponentList(unitListId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlComponentList + '(' + unitListId + ')';
        return this.http.delete(url, {
            observe: 'response'
        }).pipe(
            tap(() => {
                if (this.unitListsChanged) {
                    this.unitListsChanged.next(void 0);
                }
            }), map((result) => {
                return result.status === 204; // No Content
            })
        );
    }

    public saveOpenUnit(unit: TaskUnit): Observable<any> {
        const url = this.urlIwmBackend + this.urlSaveUnit.replace('{0}', unit.Id);
        return this.http.put(url, unit.toBackendEntity());
    }

    public saveListUnit(unit: TaskUnit): Observable<any> {
        const url = this.urlIwmBackend + this.urlSaveListUnit.replace('{0}', unit.Id);
        return this.http.put(url, unit.toBackendEntity());
    }

    public saveOpenComponent(component: TaskComponent): Observable<any> {
        const payload = { ...component };
        delete payload.ComponentType;
        delete payload.NewSuppliedEntryDescription;


        const url = `${this.urlIwmBackend}OpenComponents/${(component.Id)}`
        return this.http.put(url, payload);
    }

    public saveListComponent(componentId: string, amount: number, price: number, sendPrice: boolean): Observable<any> {
        const url = `${this.urlIwmBackend}ListComponents/${(componentId)}`

        let body = null;
        if (sendPrice) {
            body = {
                Price: price,
                SuppliedAmount: amount
            };
        } else {
            body = {
                SuppliedAmount: amount
            }
        }

        return this.http.patch(url, body);
    }

    public saveInventoryComponent(componentId: string, amount: number, price: number, sendPrice: boolean): Observable<any> {
        const url = `${this.urlIwmBackend}${this.urlInventoryComponents}/${(componentId)}`

        let body = null;
        if (sendPrice) {
            body = {
                Price: price,
                SuppliedAmount: amount
            };
        } else {
            body = {
                SuppliedAmount: amount
            }
        }

        return this.mapICToTaskComponent(this.http.patch(url, body));
    }

    public createOpenUnit(unit: TaskUnit): Observable<any> {
        const url = this.urlIwmBackend + this.urlCreateUnit;
        return this.http.post(url, unit.toBackendEntity());
    }

    public createListUnit(unit: TaskUnit): Observable<any> {
        const url = this.urlIwmBackend + this.urlCreateListUnit;
        return this.http.post(url, unit.toBackendEntity());
    }

    public createOpenComponent(component: TaskOpenComponent): Observable<any> {
        var body = { ...component };
        delete body.NewSuppliedEntryDescription;

        const url = this.urlIwmBackend + this.urlOpenComponents + '?$expand=CostAccountings';
        return this.http.post(url, body);
    }

    public createListComponent(source: any) {
        const url = this.urlIwmBackend + this.urlListComponents + '/Default.CreateComponent?$expand=CostAccountings';

        const body = {
            sourceComponentId: source.id,
            componentListId: source.componentListId
        }

        return this.http.post(url, body);

    }

    public resetComponentPrices(componentIds: string[]): Observable<TaskComponent[]> {
        const url = this.urlIwmBackend + this.urlResetComponentPrice;

        let data = {
            "componentIds": componentIds
        }

        return this.http.post(url, data).pipe(map((x: any) => x?.value));
    }

    public resetInventoryComponentPrices(componentIds: string[]): Observable<TaskComponent[]> {
        const url = this.urlIwmBackend + this.urlResetInventoryComponentPrice;

        let data = {
            "componentIds": componentIds
        }

        return this.mapICArrayToTaskComponentArray(this.http.post(url, data));
    }

    public resetUnitPrice(unitId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlResetUnitPrice.replace('{0}', unitId);
        const resp = this.http.post(url, {})
            .pipe(
                catchError(this.handleError(unitId))
            );
        return resp;
    }

    public resetUnitWorktime(unitId: string): Observable<any> {
        const url = this.urlIwmBackend + `ListUnits(${unitId})/Default.RestoreUnitWorktime`;
        const resp = this.http.post(url, {})
            .pipe(
                catchError(this.handleError(unitId))
            );
        return resp;
    }

    public removeFromPaymentPlan(unitList: TaskUnitList): Observable<any> {
        const url = `${this.urlIwmBackend + this.urlUnitList}(${unitList.Id})/Default.RemoveFromPaymentPlan`;

        return this.http.post<boolean>(url, {}, {
            observe: 'response'
        }).pipe(
            map((result) => {
                return result.status === 204; // No Content
            })
        );
    }


    /**
    * Gets the pricelists
    */
    public getPricelists(companyId: string = null, includeArchived: boolean = false, includeComponentPriceLists: boolean = true): Observable<Array<PriceList>> {

        let uri = this.urlGetPriceLists + `/Default.GetAvailablePriceLists`
        if (includeArchived) {
            uri = this.urlGetPriceLists + `/Default.GetAvailableAndArchivedPriceLists`
        }
        if (companyId) {
            uri += `?companyId=${companyId}`;
        }
        if (!includeComponentPriceLists) {
            uri += companyId ? '&includeComponentPriceLists=false' : '?includeComponentPriceLists=false';
        }

        return this.http.get<{ value: Array<PriceList> }>(uri)
            .pipe(map(res => res.value));
    }

    /**
    * Gets the pricelist
    */
    public getPricelist(priceListId: string): Observable<PriceList> {
        return this.getPricelists(null, true).pipe(
            map(res => {
                let pricelist = res.find((o) => o.Id == priceListId);
                return pricelist;
            })
        );
    }

    /**
    * Gets the invoicing proposals
    * @param taskId taskId
    * @returns returns invoising proposals by task Id
    */
    public getInvoicingProposals(taskId: string): Observable<InvoiceProposalODataResult> {
        return this.http.get<InvoiceProposalODataResult>(`${this.config.backendIwmUri}` + this.urlInvoicingProposal.replace('{0}', taskId));
    }

    public getTaskListUnits(taskId: string): Observable<any> {
        return this.http.get(`${this.config.backendIwmUri}` + this.urlTaskListUnits.replace('{0}', taskId));
    }

    public getTaskOpenUnits(taskId: string): Observable<any> {
        return this.http.get(`${this.config.backendIwmUri}` + this.urlTaskOpenUnits.replace('{0}', taskId));
    }

    public getConstructionUnit(unitId: string): Observable<any> {
        const url = this.amgUnitUrl.replace('{0}', unitId);
        return this.http.get(url);
    }

    public getConstructionUnitExpanded(unitId: string, priceListId: string): Observable<any> {
        let url = this.config.backendAmgUri + `Api/Units(${unitId})/Default.GetConstructionUnit?%24expand=Group%2CVersion`;
        if (priceListId) {
            url += `&priceListId=${priceListId}`;
        }
        else {
            url += `,CompanySpecificNotes`
        }
        return this.http.get(url)
            .pipe(
                catchError(this.handleConstructionUnitError()),
                map(res => { return res }))
    }

    public getConstructionComponentExpanded(componentId: string, priceListId: string): Observable<any> {
        let url = this.config.backendAmgUri + `Api/Components(${componentId})/Default.GetConstructionComponent?%24expand=Group%2CVersion,CompanySpecificNotes`;

        if (priceListId) {
            url += `&priceListId=${priceListId}`;
        }

        return this.http.get(url)
            .pipe(
                catchError(this.handleConstructionUnitError()),
                map(res => { return res }))
    }

    /**
     * Get inventory balance for inventory items
     * @param componentId
     * @returns
     */
    public getInventoryItemsBalanceByInventoryId(inventoryId: string): Observable<any[]> {
        const url = this.config.backendIwmUri + this.urlInventoryApi + this.urlUsableInventoryItems +
            '?$select=Id,InventoryBalance&$filter=InventoryId eq ' + inventoryId;
        return this.http.get(url)
            .pipe(
                map((x: any) => x?.value),
                catchError(this.handleConstructionUnitError()));
    }

    /**
     * Get inventory balance for single inventory item
     * @param componentId
     * @returns
     */
    public getInventoryItemBalance(sourceItemId: string): Observable<number> {
        const url = `${this.config.backendIwmUri}InventoryApi/UsableInventoryItems(${sourceItemId})?$select=InventoryBalance`;
        return this.http.get(url)
            .pipe(
                map((data: any) => data.InventoryBalance),
                tap({
                    error: e => {
                        if (e instanceof HttpErrorResponse && e.status === 404) {
                            return null;
                        }
                    }
                }),
                catchError(this.handleConstructionUnitError()))
    }

    /**
     * Get end customer Invoices
     * @param taskId
     * @returns
     */
    public getEndCustomerInvoices(taskId: string): Observable<EndCustomerInvoiceODataResult> {
        return this.http.get<EndCustomerInvoiceODataResult>(`${this.config.backendIwmUri}` + this.urlEndCustomerInvoice.replace('{0}', taskId));
    }


    /**
     * Check if some one of the task companies has license for end customer invoice
     * @param taskId
     * @returns
     */
    public checkEndCustomerInvoiceLicense(taskId: string): Observable<endCustomerLicense> {
        return this.http.get<endCustomerLicense>(`${this.config.backendIwmUri}` + this.endCustomerInvoiceLicenseUrl.replace('{0}', taskId));
    }

    /**
    * Gets the total sum of all rows for admin units for EndCustomerInvoice with given endCustomerInvoiceId
    */
    public getAAdminInvoicingUnitsTotalSum(endCustomerInvoiceId: string): Observable<number> {

        const url = `${this.config.backendIwmUri}/EndCustomerInvoiceApi/EndCustomerInvoicingUnits/GetTotalSum(endCustomerInvoiceId=${endCustomerInvoiceId})`;


        return this.http.get<any>(url).pipe(map(t => {
            return t.value;
        }));
    }

    /**
     * Gets the total sum of all rows for task units for EndCustomerInvoice with given endCustomerInvoiceId
     */
    public getInvoicingTaskUnitsTotalSum(endCustomerInvoiceId: string): Observable<number> {
        const url = `${this.config.backendIwmUri}/EndCustomerInvoiceApi/EndCustomerInvoicingTaskUnits/GetTotalSum(endCustomerInvoiceId=${endCustomerInvoiceId})`;

        return this.http.get<any>(url).pipe(map(t => {
            return t.value;
        }));
    }


    /***
        * Divides given unitlist to multiple unitlists according to scheduled payments
        */
    public divideToPaymentSchedule(unitListId: string): Observable<boolean> {
        return this.http.post<boolean>(`${this.urlIwmBackend}UnitLists(${unitListId})/Default.DivideToPaymentSchedules`, null, {
            observe: 'response'
        })
            .pipe(
                map((result) => {
                    return result.status === 204; // No Content
                }),
                catchError((err: any, caught: any) => {
                    return of(false);
                })
            )
    }

    public getTaskUnitCostAccountingsForTaskUnitList(taskId: string, unitListId: string): Observable<any> {
        return this.http.get(
            `${this.config.backendIwmUri}` +
            `Api/TaskUnitCostAccountings?$filter=TaskId eq ${taskId} and UnitListId eq ${unitListId}`
        ).pipe(
            map((x: any) => x?.value)
        );

    }

    public getTaskComponentCostAccountingsForTaskComponentList(taskId: string, componentListId: string): Observable<any> {
        return this.http.get(`${this.config.backendIwmUri}` + `Api/TaskComponentCostAccountings?$filter=TaskId eq ${taskId} and ComponentListId eq ${componentListId}`)
            .pipe(map((x: any) => x?.value));
    }

    public copyListUnitAmounts(unitId: string[], copiedAmount: string, planned: boolean, ordered: boolean, supplied: boolean): Observable<any> {
        let unitIdurl = "";

        for (let i = 0; i < unitId.length; i++) {
            const id = unitId[i];
            unitIdurl = i == 0 ? `${unitIdurl}?unitId=${id}` : `${unitIdurl}&unitId=${id}`;
        }

        const url =
            `${this.urlIwmBackend}ListUnits/CopyListUnitAmounts${unitIdurl}&copiedAmount=${copiedAmount}&planned=${planned}&ordered=${ordered}&supplied=${supplied}`;

        return this.http.post(url, {});
    }

    public copyOpenUnitAmounts(unitId: string[], copiedAmount: string, planned: boolean, ordered: boolean, supplied: boolean): Observable<any> {
        var unitIdurl = "";

        for (let i = 0; i < unitId.length; i++) {
            const id = unitId[i];
            unitIdurl = i == 0 ? `${unitIdurl}?unitId=${id}` : `${unitIdurl}&unitId=${id}`;
        }

        const url =
            `${this.urlIwmBackend}OpenUnits/CopyOpenUnitAmounts${unitIdurl}&copiedAmount=${copiedAmount}&planned=${planned}&ordered=${ordered}&supplied=${supplied}`;
        return this.http.post(url, {});
    }

    public getAvailableInventories(): Observable<Array<Inventory>> {
        return this.http
            .get(
                this.urlIwmBackend + this.urlComponentList + '/GetAvailableInventories'
            )
            .pipe(map((x: any) => x?.value));
    }

    public checkInventoryProductLicenseForTask(taskId: string): Observable<boolean> {
        const url = this.urlIwmBackend + this.urlInventoryProductLicense.replace('{0}', taskId);
        return this.http.get(url)
            .pipe(map((x: any) => x?.value));
    }

    /**
    * Gets an inventory by Id
    * @param inventoryId Id of the inventory
    * @returns The inventory
    */
    public getInventory(inventoryId: string): Observable<Inventory> {
        const url = this.config.backendIwmUri + this.urlInventoryApi + this.urlInventories + inventoryId;
        return this.http.get(url).pipe(map((x: any) => x?.value));
    }

    /**
     * Gets inventory items based on inventory id
     * @param inventoryId Id of the inventory
     * @returns Array of inventory items
     */
    public getUsableInventoryItems(inventoryId: string): Observable<UsableInventoryItem[]> {
        const url = this.config.backendIwmUri + this.urlInventoryApi + this.urlUsableInventoryItems + '?$filter=InventoryId eq ' + inventoryId;
        return this.http.get(url).pipe(map((x: any) => x?.value)).pipe(
            map((x) => {
                let items: UsableInventoryItem[] = x;
                items.forEach((i) => {
                    i.GroupName = i.InventoryFolderName;
                    i.SearchValue = `${i.Identifier} ${i.Name} ${i.SerialNumber}`;
                });
                return items.sort((a, b) =>
                    a.SearchValue.localeCompare(b.SearchValue)
                );;
            })
        );
    }

    /**
     * Create inventory component
     * @param source Object with id and component list id properties
     * @return An observable of the result
     */
    public createInventoryComponent(source: any): Observable<any> {
        const url = this.urlIwmBackend + this.urlInventoryComponents + '/Default.CreateComponent?$expand=CostAccountings';

        const body = {
            sourceItemId: source.id,
            componentListId: source.componentListId
        }

        return this.mapICToTaskComponent(this.http.post(url, body));
    }

    private mapICToTaskComponent(x: Observable<any>): Observable<TaskComponent> {
        return x.pipe(
            map((component: any) => {
                component.NewSuppliedEntryDescription = '';
                component.IsPriceListPrice = component.IsInventoryPrice;
                component.SourceComponentId = component.SourceItemId;
                component.ComponentType = ComponentType.InventoryComponent;
                delete component.IsInventoryPrice;
                return component;
            })
        );
    }

    private mapICArrayToTaskComponentArray(x: Observable<any>): Observable<TaskComponent[]> {
        return x.pipe(map((x: any) => x?.value),
            map((components: Array<TaskComponent>) => {
                return components.map((component: any) => {
                    component.NewSuppliedEntryDescription = '';
                    component.IsPriceListPrice = component.IsInventoryPrice;
                    component.SourceComponentId = component.SourceItemId;
                    component.ComponentType = ComponentType.InventoryComponent;
                    delete component.IsInventoryPrice;
                    return component;
                });
            }))
    }

    /**
     * Handling statuscode 404 when restoring prices and sourceunit is not found in unitlist's pricelist
     */
    private handleError(unitId: string) {
        return (err: any) => {
            if (err instanceof HttpErrorResponse && err.status === 404 && err.statusText === 'OK') {
                // console.error(`ResetUnitPrice status ${err.status} ${err.statusText}, sourceunit ${unitId} not found.`);
                return of(null);
            } else {
                return throwError(() => new Error(err));
            }
        }
    }

    /**
    * Handling error statuscodes
    */
    private handleConstructionUnitError() {
        return (err: any) => {
            if (err instanceof HttpErrorResponse) {
                return of(null);
            }
            else {
                return throwError(() => new Error(err));
            }
        }
    }
}
